import type { ComponentType } from 'react';
import type { AuthedUser } from './auth-types';

// Single source of truth für die Import-Factories aller lazy pages. Wird sowohl
// von App.tsx (für React.lazy via lazyWithRetry) als auch von preloadRoutes.ts
// (für Idle-Preload via dynamic import) genutzt. Verhindert dual-string-source:
// ändert sich der Pfad zu einer Page, ist nur EINE Stelle anzupassen.
//
// Named-export-Workaround: Pages exportieren named (`export function X`), React.lazy
// erwartet einen { default: Component }-Promise. Die .then-Mapper hier sind die
// gleichen wie vorher inline in App.tsx waren.

type LazyPage = ComponentType<unknown>;
type LazyPageWithId = ComponentType<{ id: string }>;
type LazyPageWithAuthedUser = ComponentType<{ authedUser: AuthedUser }>;

export const lazyPageFactories = {
  AssetDetailPage: () =>
    import('../pages/AssetDetailPage').then((m) => ({
      default: m.AssetDetailPage as LazyPageWithId,
    })),
  TrashPage: () =>
    import('../pages/TrashPage').then((m) => ({
      default: m.TrashPage as LazyPage,
    })),
  // Roadmap 3a Phase 2: dedizierte Favoriten-Seite. Niedrige Frequenz → NICHT in
  // preloadRoutes PRELOAD_TARGETS.
  FavoritesPage: () =>
    import('../pages/FavoritesPage').then((m) => ({
      default: m.FavoritesPage as LazyPage,
    })),
  InsightsPage: () =>
    import('../pages/InsightsPage').then((m) => ({
      default: m.InsightsPage as LazyPage,
    })),
  CollectionListPage: () =>
    import('../pages/CollectionListPage').then((m) => ({
      default: m.CollectionListPage as LazyPage,
    })),
  CollectionDetailPage: () =>
    import('../pages/CollectionDetailPage').then((m) => ({
      default: m.CollectionDetailPage as LazyPageWithId,
    })),
  RecipeListPage: () =>
    import('../pages/RecipeListPage').then((m) => ({
      default: m.RecipeListPage as LazyPage,
    })),
  RecipeDetailPage: () =>
    import('../pages/RecipeDetailPage').then((m) => ({
      default: m.RecipeDetailPage as LazyPageWithId,
    })),
  // Sprint C1: SharesListPage. NICHT in preloadRoutes.ts PRELOAD_TARGETS —
  // niedrigere expected-Frequenz als AssetDetail/CollectionList.
  SharesListPage: () =>
    import('../pages/SharesListPage').then((m) => ({
      default: m.SharesListPage as LazyPage,
    })),
  // Händler-Links (Spec Phase 1): Verwaltungsliste /public-links. Niedrige
  // Frequenz — NICHT in PRELOAD_TARGETS. Nimmt `authedUser` (Depublish-Gating).
  PublicLinksPage: () =>
    import('../pages/PublicLinksPage').then((m) => ({
      default: m.PublicLinksPage as LazyPageWithAuthedUser,
    })),
  // P1c PR-C: LoginPage, ProfilePage und TeamPage sind hier RAUS — Login und
  // Identität leben auf tools.greenforce.com. TeamPage/InviteUserModal bleiben
  // als Dateien bestehen (der tools-Client importiert sie); ProfilePage wurde
  // gelöscht, DAM war ihr einziger Konsument.
  // Sprint C5 (2026-05-18): Brand Center MVP. Read-only Hub /brand für alle
  // auth-User. Admin-only Curation /brand/admin (Edit + Delete + Upload via Modals).
  BrandPage: () =>
    import('../pages/BrandPage').then((m) => ({
      default: m.BrandPage as LazyPageWithAuthedUser,
    })),
  BrandAdminPage: () =>
    import('../pages/BrandAdminPage').then((m) => ({
      default: m.BrandAdminPage as LazyPageWithAuthedUser,
    })),
  // Taxonomie Phase 1: Vokabular-Admin (/settings/vocab). Admin-only, niedrige
  // Frequenz — NICHT in PRELOAD_TARGETS.
  VocabAdminPage: () =>
    import('../pages/VocabAdminPage').then((m) => ({
      default: m.VocabAdminPage as LazyPageWithAuthedUser,
    })),
} as const;

export type LazyPageName = keyof typeof lazyPageFactories;
